/**
 * Alarm "where" resolution (A2).
 *
 * An alarm must show WHERE in the process it fired — the Tier-2 twin
 * equipment the breaching tag is bound to ("Induction Furnace") and that
 * entity's process stage ("Heat"), not just the raw tag/device slug.
 *
 * Two layers, in priority order:
 *   1. Backend-resolved — the alarm event's message carries `entity_name` +
 *      `stage` (see platform-core .../alarms_rule_engine.go). `toAlarmItem`
 *      lifts those onto {@link AlarmItem.entityName} / `.stage`. This is the
 *      authoritative path once the API ships A2.
 *   2. Client-side fallback — when the backend fields are absent (older API,
 *      not-yet-redeployed), resolve via the org's already-loaded plant: match
 *      the alarm's `tagId` against the entity_tag_bindings (tag → entity), then
 *      read that entity's name + tracked-output stage label. Degrades to the
 *      tag/device `source` when the tag isn't bound to any entity.
 *
 * Both layers are org-scoped (the queries hit the active-org `/v1/twin/*`
 * endpoints) and purely additive — a tag with no binding shows the `source`
 * exactly as before.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  isLiveApi,
  twinBindings,
  twinEntities,
  type TwinBinding,
  type TwinEntity,
} from "@/lib/api";
import type { AlarmItem } from "@/lib/widgets";

/** The resolved location of an alarm. Both fields optional. */
export interface AlarmWhere {
  /** Bound Tier-2 entity (equipment) name, e.g. "Induction Furnace". */
  entityName?: string;
  /** That entity's process stage label, e.g. "Heat". */
  stage?: string;
}

/** A resolved location plus whether there's anything to show. */
export interface ResolvedAlarmWhere extends AlarmWhere {
  /** True when at least an entity name is known — gate rendering on this. */
  hasWhere: boolean;
  /** One-line label, e.g. "Induction Furnace · Heat" (or just the entity). */
  label?: string;
}

/** Read an entity's stage label from its `data.entity.trackedOutput.label`. */
function stageFromEntity(e: TwinEntity): string | undefined {
  const data = e.data;
  if (!data || typeof data !== "object") return undefined;
  const entity = (data as { entity?: unknown }).entity;
  if (!entity || typeof entity !== "object") return undefined;
  const tracked = (entity as { trackedOutput?: unknown }).trackedOutput;
  if (!tracked || typeof tracked !== "object") return undefined;
  const label = (tracked as { label?: unknown }).label;
  return typeof label === "string" && label.trim() !== "" ? label : undefined;
}

/**
 * Build `tagId → AlarmWhere` from the org's bindings + entities. A tag may be
 * bound to more than one entity; the bindings list is server-ordered
 * (primary + stream first), so the FIRST binding seen per tag wins — matching
 * the backend's deterministic pick.
 */
function buildTagWhereMap(
  bindings: TwinBinding[],
  entities: TwinEntity[],
): Map<string, AlarmWhere> {
  const byId = new Map<string, TwinEntity>();
  for (const e of entities) byId.set(e.id, e);

  const out = new Map<string, AlarmWhere>();
  for (const b of bindings) {
    if (out.has(b.tag_id)) continue; // first (highest-priority) binding wins
    const entity = byId.get(b.entity_id);
    if (!entity) continue;
    out.set(b.tag_id, {
      entityName: entity.name,
      stage: stageFromEntity(entity),
    });
  }
  return out;
}

/** Compose the one-line "where" label from an entity name + optional stage. */
export function whereLabel(w: AlarmWhere): string | undefined {
  if (!w.entityName) return undefined;
  return w.stage ? `${w.entityName} · ${w.stage}` : w.entityName;
}

/**
 * Resolver hook for the alarms surface. Returns `resolve(alarm)` which yields
 * the alarm's location, preferring the backend-provided fields and falling
 * back to the bindings/entities lookup keyed by the alarm's `tagId`.
 *
 * In stub mode (no live API) the queries are disabled, so resolution relies
 * solely on whatever the AlarmItem already carries (fixtures set neither, so
 * `hasWhere` is false and the source line shows as today).
 */
export function useAlarmWhereResolver(): (alarm: AlarmItem) => ResolvedAlarmWhere {
  const bindingsQuery = useQuery({
    queryKey: ["twin-bindings", "all"],
    queryFn: () => twinBindings.list(),
    enabled: isLiveApi,
    staleTime: 60_000,
  });
  const entitiesQuery = useQuery({
    queryKey: ["twin-entities"],
    queryFn: () => twinEntities.list({ limit: 200 }),
    enabled: isLiveApi,
    staleTime: 60_000,
  });

  const tagWhere = useMemo(
    () =>
      buildTagWhereMap(
        bindingsQuery.data?.items ?? [],
        entitiesQuery.data?.items ?? [],
      ),
    [bindingsQuery.data, entitiesQuery.data],
  );

  return useMemo(() => {
    return (alarm: AlarmItem): ResolvedAlarmWhere => {
      // Backend-resolved fields take precedence; fall back to the lookup.
      const fallback = alarm.tagId ? tagWhere.get(alarm.tagId) : undefined;
      const entityName = alarm.entityName ?? fallback?.entityName;
      const stage = alarm.stage ?? fallback?.stage;
      const w: AlarmWhere = { entityName, stage };
      return { ...w, hasWhere: Boolean(entityName), label: whereLabel(w) };
    };
  }, [tagWhere]);
}
